/**
 * The ordered goods leave the Shopify cart, once per order and only in the browser that ordered them (BEST-493,
 * BEST-542). Loaded on every page, before bestio-checkout.js, which hands its orders over to it.
 *
 * - A gateway payment (GoPay) has no order when the customer leaves for the gateway. The checkout remembers the payment
 *   and the cart lines it was built from (`bestio_checkout_pending_payments`, localStorage, at most 5, 24 hours like
 *   `CheckoutService.PaymentReturnLifetime`). The customer may finish it anywhere: in the bank app, in another tab or
 *   browser, or not at all. Every storefront page outside the checkout asks the Checkout API about the newest payment
 *   not asked in the last 30 seconds (`GET /api/v1/checkout/payment-status?payment=`, no cookie, so the answer is that
 *   payment alone, BEST-504). An order takes its lines out of the cart; a failed, cancelled or unknown payment forgets
 *   the record and leaves the cart; a payment still running is asked again later.
 * - Taking out means subtracting the ordered quantity of each variant (the remembered line first), so goods added after
 *   the order stay. Subtracting twice would take them too, so the order is claimed in `bestio_checkout_cleared_orders`
 *   before the cart changes, under a Web Lock shared by the tabs; without Web Locks storefront pages leave it to the
 *   thank-you page. A refused change gives the claim back; a change whose result is unknown keeps it (the ordered goods
 *   may stay in the cart, which is the lesser harm). A browser without localStorage keeps the claims in a cookie (the
 *   Shopify cart itself lives in a cookie); a browser that cannot keep a claim at all changes nothing.
 * - Shopify's cart API writes absolute quantities, so the subtraction reads the cart and writes what is left. Every other
 *   cart change of the page (`fetch` POST to cart/add, change, update or clear, whichever script sends it) takes the same
 *   Web Lock in shared mode: changes run side by side as before, but none of them, in any tab, lands between the read and
 *   the write of a subtraction, which would otherwise write back the quantity it read and drop the added piece.
 * - The whole cart is emptied only at the confirmation of the checkout (`clearWhenUnknown`) when its lines are unknown,
 *   once per order and only after the claim; an emptying that failed is not tried again, because by the next visit the
 *   cart may hold new goods. The thank-you page takes out the lines it knows (the tab's summary, the remembered payment
 *   or the order lines of the Checkout API) and leaves the cart alone without them.
 */
(() => {
  'use strict';

  const PENDING_STORAGE_KEY = 'bestio_checkout_pending_payments';
  const PENDING_LIMIT = 5;
  const PENDING_TTL_MS = 24 * 60 * 60 * 1000;
  const CHECK_INTERVAL_MS = 30 * 1000;
  // Orders whose goods already left the cart (BEST-493): ids as strings, the newest 20.
  const CLEARED_STORAGE_KEY = 'bestio_checkout_cleared_orders';
  const CLEARED_LIMIT = 20;
  // The same claims in a cookie for a browser without localStorage: short, the newest 10, for 60 days.
  const CLEARED_COOKIE = 'bestio_cleared_orders';
  const CLEARED_COOKIE_LIMIT = 10;
  const CLEARED_COOKIE_MAX_AGE_S = 60 * 24 * 60 * 60;
  const REQUEST_TIMEOUT_MS = 10000;
  // How long a subtraction waits for the cart changes running in this and other tabs before it tries again later.
  const LOCK_WAIT_MS = 15000;
  const CART_CHANGE_PATH = /\/cart\/(?:add|change|update|clear)(?:\.js)?$/;
  const PAYMENT_ID = /^[0-9a-f]{32}$/;
  // A gateway order whose purchase could not be measured yet (the Checkout API had no readable order, or could not decide the
  // claim): asked again on later pages without touching the cart, at most 8 times within the payment's 24 hours.
  const MEASUREMENT_STORAGE_KEY = 'bestio_checkout_pending_measurements';
  const MEASUREMENT_ATTEMPTS = 8;
  // The browser's claimant of purchases (bestio-hub Checkout `claimant=`): a random pseudonymous id, kept in localStorage
  // only with consent to analytics or marketing and for 24 hours at most (so it does not link the orders of a device);
  // without consent one per page, nothing kept.
  const CLAIMANT_STORAGE_KEY = 'bestio_purchase_claimant';
  const CLAIMANT_TTL_MS = 24 * 60 * 60 * 1000;
  // The statuses of a payment that went through (bestio-hub Checkout Core `PaymentStatus`): only such a payment's lines
  // belong to the order of its checkout. A failed payment's checkout may have been paid later by another payment with
  // other lines (BEST-542).
  const PAYMENT_TAKEN_STATUSES = Object.freeze(['Paid', 'Refunded', 'PartiallyRefunded']);
  const LOCK_NAME = 'bestio-ordered-cart';

  const script = document.currentScript;
  const routeRoot = () => String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  // A localhost storefront (theme dev) talks to the local Checkout host, as the checkout does.
  const isLocalStorefront = ['127.0.0.1', 'localhost'].includes(window.location.hostname);
  const apiBase = (isLocalStorefront
    ? `${window.location.protocol}//${window.location.hostname}:8200`
    : String(script?.dataset.checkoutApiBaseUrl || '')).replace(/\/$/, '');

  /** Only codes and statuses in the console; never payment ids, order ids or cart contents. */
  const logFailure = (operation, detail = {}) => console.warn('Bestio ordered cart step failed.', {operation, ...detail});

  // --- Cart changes of the page wait for a running subtraction -------------------------------------------------------
  // The page's own fetch: the subtraction sends its cart requests with it, outside the shared lock it holds exclusively.
  const pageFetch = typeof window.fetch === 'function' ? window.fetch : null;
  const locks = () => (window.navigator?.locks?.request ? window.navigator.locks : null);

  /** Whether a fetch is a change of the Shopify cart on this storefront (a POST to cart/add, change, update or clear). */
  function isCartChange(input, init) {
    try {
      const method = String(init?.method || (typeof input === 'object' && input?.method) || 'GET').toUpperCase();
      if (method !== 'POST') return false;
      const raw = typeof input === 'object' && input?.url ? input.url : String(input);
      const url = new URL(raw, `${window.location.protocol}//${window.location.host || window.location.hostname}`);
      return url.hostname === window.location.hostname && CART_CHANGE_PATH.test(url.pathname);
    } catch (_) {
      return false;
    }
  }

  if (pageFetch && locks()) {
    window.fetch = function bestioCartAwareFetch(input, init) {
      if (!isCartChange(input, init)) return pageFetch.call(this, input, init);
      let sent = false;
      return locks().request(LOCK_NAME, {mode: 'shared'}, () => {
        sent = true;
        return pageFetch.call(window, input, init);
      }).catch(error => {
        // The lock itself failed (an embedded frame without Web Locks): the change goes as it would without this script.
        if (sent) throw error;
        return pageFetch.call(window, input, init);
      });
    };
  }

  // --- Storage ----------------------------------------------------------------------------------------------------
  /** The stored list, [] for a missing or damaged value, or null when the browser keeps no storage. */
  function readList(key) {
    let raw;
    try { raw = localStorage.getItem(key); } catch (_) { return null; }
    try {
      const parsed = JSON.parse(raw || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  function writeList(key, list) {
    try { localStorage.setItem(key, JSON.stringify(list)); return true; } catch (_) { return false; }
  }

  /** The claims kept in the cookie, [] without the cookie, or null when the browser keeps no cookies. */
  function readClearedCookie() {
    try {
      if (window.navigator?.cookieEnabled === false) return null;
      const entry = String(document.cookie || '').split(/;\s*/).find(part => part.startsWith(`${CLEARED_COOKIE}=`));
      return entry ? decodeURIComponent(entry.slice(CLEARED_COOKIE.length + 1)).split(',').filter(Boolean) : [];
    } catch (_) {
      return null;
    }
  }

  /** Writes the claims to the cookie; true only when the cookie reads them back. */
  function writeClearedCookie(list) {
    const kept = list.slice(-CLEARED_COOKIE_LIMIT);
    try {
      const secure = window.location.protocol === 'https:' ? '; secure' : '';
      document.cookie = `${CLEARED_COOKIE}=${encodeURIComponent(kept.join(','))}; path=/; max-age=${CLEARED_COOKIE_MAX_AGE_S}; samesite=lax${secure}`;
    } catch (_) {
      return false;
    }
    const stored = readClearedCookie();
    return Boolean(stored) && stored.length === kept.length && kept.every(id => stored.includes(id));
  }

  /** The orders whose goods already left the cart (localStorage and the cookie), or null when neither can be read. */
  function clearedOrders() {
    const stored = readList(CLEARED_STORAGE_KEY)?.map(String) ?? null;
    const cookie = readClearedCookie();
    if (!stored && !cookie) return null;
    return [...new Set([...(stored || []), ...(cookie || [])])];
  }

  /** Claims the order; false when no storage keeps the claim (the caller then changes nothing). */
  function rememberClearedOrder(orderId) {
    const id = String(orderId || '');
    const cleared = clearedOrders();
    if (!id || !cleared) return false;
    if (cleared.includes(id)) return true;
    const stored = readList(CLEARED_STORAGE_KEY);
    if (stored && writeList(CLEARED_STORAGE_KEY, [...stored.map(String), id].slice(-CLEARED_LIMIT))) return true;
    return writeClearedCookie([...(readClearedCookie() || []), id]);
  }

  function forgetClearedOrder(orderId) {
    const id = String(orderId);
    const stored = readList(CLEARED_STORAGE_KEY);
    if (stored) writeList(CLEARED_STORAGE_KEY, stored.map(String).filter(item => item !== id));
    const cookie = readClearedCookie();
    if (cookie?.includes(id)) writeClearedCookie(cookie.filter(item => item !== id));
  }

  const variantNumber = id => /(\d+)$/.exec(String(id ?? ''))?.[1] || null;

  /**
   * Ordered lines in one shape, from Shopify cart lines (`key`, `variant_id`), from the checkout's lines
   * (`shopifyVariantId`) or from a stored record (`variantId`).
   */
  function orderedLines(lines) {
    return (Array.isArray(lines) ? lines : []).map(line => ({
      key: typeof line?.key === 'string' && line.key ? line.key : null,
      variantId: variantNumber(line?.variantId ?? line?.variant_id ?? line?.shopifyVariantId),
      quantity: Math.floor(Number(line?.quantity)),
    })).filter(line => line.variantId && line.quantity > 0);
  }

  const paymentIdOf = value => {
    const id = String(value || '').toLowerCase();
    return PAYMENT_ID.test(id) ? id : null;
  };

  /** The remembered payments still worth asking about, or null without storage. A damaged entry is left out. */
  function pendingPayments(now = Date.now()) {
    const stored = readList(PENDING_STORAGE_KEY);
    if (!stored) return null;
    return stored.filter(record => paymentIdOf(record?.paymentId) && Number.isFinite(record.savedAt) && now - record.savedAt < PENDING_TTL_MS
      && orderedLines(record.lines).length);
  }

  function updatePending(change) {
    const list = pendingPayments();
    if (!list) return false;
    return writeList(PENDING_STORAGE_KEY, change(list));
  }

  /** At the redirect to the gateway: the payment and the cart lines its order will have. */
  function rememberPayment(paymentId, lines, marketCode) {
    const id = paymentIdOf(paymentId);
    const ordered = orderedLines(lines);
    if (!id || !ordered.length) return false;
    // The market is kept for diagnosis only: DE, AT and HU share the cart and the API of bestio.cz.
    const record = {paymentId: id, marketCode: String(marketCode || ''), lines: ordered, savedAt: Date.now(), checkedAt: 0};
    return updatePending(list => [...list.filter(item => item.paymentId !== id), record].slice(-PENDING_LIMIT));
  }

  const findPayment = paymentId => {
    const id = paymentIdOf(paymentId);
    return id ? pendingPayments()?.find(record => record.paymentId === id) ?? null : null;
  };
  const hasPayment = paymentId => Boolean(findPayment(paymentId));
  const forgetPayment = paymentId => {
    const id = paymentIdOf(paymentId);
    if (id) updatePending(list => list.filter(record => record.paymentId !== id));
  };

  // --- Purchases still to measure and the browser's claimant ---------------------------------------------------------
  let pageClaimant = null;
  function randomHex() {
    const bytes = new Uint8Array(16);
    try { window.crypto.getRandomValues(bytes); } catch (_) { for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256); }
    return [...bytes].map(byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * The id this page claims purchases with. With consent the browser's kept id (24 hours from its creation, then a new one),
   * and the page's own id kept from then on when the consent came later; without consent the page's own id and nothing kept
   * (an id kept earlier is removed: the consent was withdrawn).
   */
  function claimant() {
    const consent = analyticsAllowed();
    // Only an answer of the banner removes a kept id; the banner's API not loaded yet is no withdrawal.
    const refused = !consent && Boolean(window.Shopify?.customerPrivacy);
    let kept = null;
    try {
      kept = JSON.parse(localStorage.getItem(CLAIMANT_STORAGE_KEY) || 'null');
      if (refused) localStorage.removeItem(CLAIMANT_STORAGE_KEY);
    } catch (_) { /* Storage unavailable or unreadable: one id per page. */ }
    const now = Date.now();
    const fresh = kept && PAYMENT_ID.test(String(kept.id)) && Number.isFinite(kept.createdAt) && now - kept.createdAt < CLAIMANT_TTL_MS;
    // A page open for more than 24 hours takes a new id too, so no id outlives the limit.
    if (!pageClaimant || now - pageClaimant.createdAt >= CLAIMANT_TTL_MS) {
      pageClaimant = consent && fresh ? {id: kept.id, createdAt: kept.createdAt} : {id: randomHex(), createdAt: now};
    }
    if (consent && !(fresh && kept.id === pageClaimant.id)) {
      try { localStorage.setItem(CLAIMANT_STORAGE_KEY, JSON.stringify(pageClaimant)); } catch (_) { /* This page only. */ }
    }
    return pageClaimant.id;
  }

  function pendingMeasurements(now = Date.now()) {
    const stored = readList(MEASUREMENT_STORAGE_KEY);
    if (!stored) return null;
    return stored.filter(record => paymentIdOf(record?.paymentId) && Number.isFinite(record.savedAt) && now - record.savedAt < PENDING_TTL_MS
      && (Number(record.attempts) || 0) < MEASUREMENT_ATTEMPTS);
  }

  /** A gateway order to measure on a later page; false without storage or without a payment id. */
  function rememberMeasurement(paymentId, orderId) {
    const id = paymentIdOf(paymentId);
    const list = pendingMeasurements();
    if (!id || !list || !orderId) return false;
    if (list.some(record => record.paymentId === id)) return true;
    return writeList(MEASUREMENT_STORAGE_KEY, [...list, {paymentId: id, orderId: String(orderId), savedAt: Date.now(), checkedAt: Date.now(), attempts: 0}]
      .slice(-PENDING_LIMIT));
  }

  /** The purchase of the payment is measured or nothing to measure: no later page reads it. */
  const forgetMeasurement = paymentId => {
    const id = paymentIdOf(paymentId);
    const list = id ? pendingMeasurements() : null;
    if (list?.some(record => record.paymentId === id)) writeList(MEASUREMENT_STORAGE_KEY, list.filter(record => record.paymentId !== id));
  };

  // --- The cart -----------------------------------------------------------------------------------------------------
  async function timed(work) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try { return await work(controller.signal); } finally { window.clearTimeout(timer); }
  }

  // The page's fetch, not the wrapped one: these requests run under the exclusive lock the wrapper would wait for.
  const cartPost = (path, body) => timed(signal => pageFetch.call(window, `${routeRoot()}${path}`, {
    method: 'POST', credentials: 'same-origin', cache: 'no-store', signal,
    headers: {Accept: 'application/json', 'Content-Type': 'application/json'}, body: JSON.stringify(body),
  }));

  /**
   * Subtracts the ordered quantity of each variant from the cart: first from the remembered line, then from other lines of
   * the variant, never more than the cart holds. `done`, `refused` (nothing changed) or `unknown` (the request may have
   * changed the cart).
   */
  async function subtract(ordered) {
    let cart;
    try {
      cart = await timed(async signal => {
        const response = await pageFetch.call(window, `${routeRoot()}cart.js`, {headers: {Accept: 'application/json'}, credentials: 'same-origin', cache: 'no-store', signal});
        if (!response.ok) throw new Error(String(response.status));
        return response.json();
      });
    } catch (error) {
      logFailure('cart-read', {reason: error?.message || null});
      return 'refused';
    }
    const left = new Map();
    ordered.forEach(line => left.set(line.variantId, (left.get(line.variantId) || 0) + line.quantity));
    const keys = new Set(ordered.map(line => line.key).filter(Boolean));
    const updates = {};
    [...(cart?.items || [])].sort((a, b) => Number(keys.has(b.key)) - Number(keys.has(a.key))).forEach(item => {
      const variant = variantNumber(item.variant_id);
      const wanted = left.get(variant) || 0;
      if (!wanted || !item.key) return;
      const taken = Math.min(wanted, Number(item.quantity) || 0);
      left.set(variant, wanted - taken);
      updates[item.key] = Number(item.quantity) - taken;
    });
    if (!Object.keys(updates).length) return 'done';
    try {
      const response = await cartPost('cart/update.js', {updates});
      if (response.ok) return 'done';
      logFailure('cart-update', {status: response.status});
      return 'refused';
    } catch (error) {
      logFailure('cart-update', {reason: error?.name === 'AbortError' ? 'timeout' : 'network'});
      return 'unknown';
    }
  }

  async function clearCart() {
    try {
      const response = await cartPost('cart/clear.js', {});
      if (response.ok) return 'done';
      logFailure('cart-clear', {status: response.status});
    } catch (error) {
      logFailure('cart-clear', {reason: error?.name === 'AbortError' ? 'timeout' : 'network'});
    }
    return 'refused';
  }

  // Not awaited: the header follows on its own, and the confirmation must not wait for it before leaving the page.
  const refreshHeader = () => {
    Promise.resolve(window.xCartHelper?.refreshFromServer?.()).catch(error => logFailure('header-refresh', {reason: error?.name || null}));
  };

  /**
   * Runs `work` under the lock shared by the tabs, exclusively: the cart changes of every tab wait for it (they take the
   * lock in shared mode). Waiting longer than LOCK_WAIT_MS for running changes skips the work for a later visit. Without
   * Web Locks the work runs only when the caller does not require the lock.
   */
  function withLock(work, required) {
    const manager = locks();
    if (!manager) return required ? Promise.resolve('skipped') : work();
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), LOCK_WAIT_MS);
    let granted = false;
    return manager.request(LOCK_NAME, {signal: controller.signal}, () => {
      granted = true;
      window.clearTimeout(timer);
      return work();
    }).catch(error => {
      if (granted) throw error;
      logFailure('lock', {reason: error?.name === 'AbortError' ? 'timeout' : error?.name || null});
      return 'skipped';
    }).finally(() => window.clearTimeout(timer));
  }

  /**
   * Takes the goods of an order out of the cart, once per order. `lines` are the ordered lines when the caller knows them
   * (the cart at the confirmation, the summary of the confirming tab, the order lines of the Checkout API); the remembered
   * payment's lines win. Without lines the whole cart is emptied only when `clearWhenUnknown` (the confirmation itself).
   * The order is claimed before the cart changes: a browser that can keep no claim changes nothing, because each visit
   * would take the goods again, also those added since. `requireLock`: do nothing without Web Locks. Resolves with done,
   * already, cleared, unknown, refused or skipped.
   */
  function settle(orderId, {paymentId = null, lines = null, clearWhenUnknown = false, requireLock = false} = {}) {
    const id = String(orderId || '');
    if (!id) return Promise.resolve('skipped');
    return withLock(async () => {
      if (clearedOrders()?.includes(id)) {
        forgetPayment(paymentId);
        return 'already';
      }
      const ordered = orderedLines(findPayment(paymentId)?.lines || lines);
      if (!ordered.length && !clearWhenUnknown) return 'skipped';
      if (!rememberClearedOrder(id)) return 'skipped';
      if (!ordered.length) {
        // An emptying that failed keeps the claim: the next visit may find goods added since, which it must not take.
        const result = await clearCart();
        forgetPayment(paymentId);
        refreshHeader();
        return result === 'done' ? 'cleared' : result;
      }
      const result = await subtract(ordered);
      if (result === 'refused') forgetClearedOrder(id);
      else forgetPayment(paymentId);
      refreshHeader();
      return result;
    }, requireLock);
  }

  // --- The storefront asks about the remembered payments ------------------------------------------------------------
  let reconciling = false;

  /**
   * A gateway payment the customer finished away from the thank-you page (the bank app, a closed tab) is measured here,
   * on the next storefront page of the browser that remembers it: the same event the thank-you page dispatches, with the
   * purchase the Checkout API read from Shopify (amounts without the goods, BEST-504). The measurement announces an order
   * once per browser (assets/bestio-analytics.js) and the container applies the consent, as on the thank-you page.
   */
  function analyticsAllowed() {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return privacy?.analyticsProcessingAllowed?.() === true || privacy?.marketingAllowed?.() === true;
    } catch (_) { return false; }
  }

  /**
   * Measures the purchase of an answer: `measured`, `elsewhere` (another page has it), `later` (no readable purchase yet, or a
   * claim the server could not decide) or `none` (no order to measure: cancelled, refunded, not created).
   */
  function announcePurchase(answer) {
    const purchase = answer.purchase;
    if (answer.status !== 'OrderCreated' || answer.orderCancelled === true || answer.orderRefunded === true) return 'none';
    if (!purchase || typeof purchase !== 'object' || answer.purchaseClaimed === null || answer.purchaseClaimed === undefined) return 'later';
    // Only the page the Checkout API handed the purchase to measures it: a thank-you page in another browser (the bank
    // app's) may have taken it already, and GA4 merges the same transaction only within one browser's user.
    if (answer.purchaseClaimed !== true) return 'elsewhere';
    try {
      document.dispatchEvent(new CustomEvent('bestio:checkout:thank-you', {detail: {orderId: answer.shopifyOrderId,
        total: answer.totalAmount ?? null, currency: answer.currencyCode ?? null, status: answer.status, cancelled: false,
        refunded: false, purchase, purchaseClaimed: true, order: null}}));
      return 'measured';
    } catch (error) {
      logFailure('purchase-announce', {reason: error?.name || null});
      return 'later';
    }
  }

  async function paymentStatus(paymentId) {
    try {
      // The page asks for the purchase and says whether the visitor allowed marketing (as the thank-you page, ADR 0028).
      // `claim=1` alone is what a Checkout API before the claimant (f96280a2) reads; `claimant` is this browser.
      let marketing = false;
      try { marketing = window.Shopify?.customerPrivacy?.marketingAllowed?.() === true; } catch (_) { /* As without consent. */ }
      const claim = `&claim=1&claimant=${claimant()}${marketing ? '&consent=1' : ''}`;
      const response = await timed(signal => fetch(`${apiBase}/api/v1/checkout/payment-status?payment=${paymentId}${claim}`, {
        credentials: 'omit', cache: 'no-store', headers: {Accept: 'application/json'}, signal,
      }));
      if (response.status === 404 || response.status === 410) return {kind: 'gone'};
      if (!response.ok) {
        logFailure('payment-status', {status: response.status});
        return {kind: 'later'};
      }
      return {kind: 'answer', body: await response.json()};
    } catch (error) {
      logFailure('payment-status', {reason: error?.name === 'AbortError' ? 'timeout' : 'network'});
      return {kind: 'later'};
    }
  }

  /**
   * One status read per page at most: the newest remembered payment not asked in the last 30 seconds by any tab. Pages of
   * the checkout own their payment and do not ask; a hidden tab asks when it is shown.
   */
  async function reconcile() {
    if (reconciling || !apiBase || document.visibilityState === 'hidden' || document.querySelector('[data-bestio-checkout]')) return 'skipped';
    const now = Date.now();
    const list = pendingPayments(now);
    if (!list) return 'skipped';
    const stored = readList(PENDING_STORAGE_KEY);
    if (stored && stored.length !== list.length) writeList(PENDING_STORAGE_KEY, list);
    const record = list.filter(item => now - (Number(item.checkedAt) || 0) >= CHECK_INTERVAL_MS).sort((a, b) => b.savedAt - a.savedAt)[0];
    if (!record) return measureLater(now);
    reconciling = true;
    try {
      updatePending(items => items.map(item => (item.paymentId === record.paymentId ? {...item, checkedAt: now} : item)));
      const status = await paymentStatus(record.paymentId);
      if (status.kind === 'gone') {
        forgetPayment(record.paymentId);
        return 'forgotten';
      }
      if (status.kind !== 'answer') return 'later';
      const answer = status.body || {};
      if (answer.paymentId && String(answer.paymentId).toLowerCase() !== record.paymentId) {
        logFailure('payment-status', {reason: 'another-payment'});
        return 'later';
      }
      const taken = PAYMENT_TAKEN_STATUSES.includes(answer.paymentStatus);
      if (answer.shopifyOrderId && taken) {
        // The cart is settled now; a purchase not measurable yet is kept apart, so a later page reads it again without
        // taking the ordered goods twice.
        if (announcePurchase(answer) === 'later') rememberMeasurement(record.paymentId, answer.shopifyOrderId);
        return settle(answer.shopifyOrderId, {paymentId: record.paymentId, requireLock: true});
      }
      // The checkout has an order this payment did not pay, or nothing was ordered and nothing runs any more (failed,
      // cancelled, a payment never created at the gateway): the record goes, the cart stays.
      if (answer.shopifyOrderId || answer.paymentInFlight === false) {
        forgetPayment(record.paymentId);
        return 'forgotten';
      }
      return 'later';
    } finally {
      reconciling = false;
    }
  }

  /** The newest purchase still to measure, not asked in the last 30 seconds: one read, the cart untouched. */
  async function measureLater(now) {
    const list = pendingMeasurements(now);
    if (!list) return 'skipped';
    const stored = readList(MEASUREMENT_STORAGE_KEY);
    if (stored && stored.length !== list.length) writeList(MEASUREMENT_STORAGE_KEY, list);
    const record = list.filter(item => now - (Number(item.checkedAt) || 0) >= CHECK_INTERVAL_MS).sort((a, b) => b.savedAt - a.savedAt)[0];
    if (!record) return 'skipped';
    reconciling = true;
    try {
      const list2 = pendingMeasurements(now) || [];
      writeList(MEASUREMENT_STORAGE_KEY, list2.map(item => (item.paymentId === record.paymentId
        ? {...item, checkedAt: now, attempts: (Number(item.attempts) || 0) + 1} : item)));
      const status = await paymentStatus(record.paymentId);
      if (status.kind === 'gone') {
        forgetMeasurement(record.paymentId);
        return 'forgotten';
      }
      if (status.kind !== 'answer') return 'later';
      const answer = status.body || {};
      if (answer.paymentId && String(answer.paymentId).toLowerCase() !== record.paymentId) return 'later';
      const outcome = announcePurchase(answer);
      if (outcome !== 'later') forgetMeasurement(record.paymentId);
      return outcome;
    } finally {
      reconciling = false;
    }
  }

  window.BestioOrderedCart = Object.freeze({rememberPayment, hasPayment, settle, reconcile, clearedOrders, claimant, rememberMeasurement,
    forgetMeasurement});

  // The first read waits for the cookie banner's API (at most 3 s), so a visitor who allowed analytics asks for the purchase.
  let started = false;
  const start = () => { if (!started) { started = true; reconcile(); } };
  if (!window.Shopify?.customerPrivacy && typeof window.Shopify?.loadFeatures === 'function') {
    window.setTimeout(start, 3000);
    try { window.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.1'}], () => start()); } catch (_) { start(); }
  } else {
    start();
  }
  // Before the first read (still waiting for the banner's API) a shown page leaves it to that read.
  window.addEventListener('pageshow', event => { if (event.persisted && started) reconcile(); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible' && started) reconcile(); });
})();
